import type { GameDefinition, Outcome, Rng, Seat, Viewer } from '@campfire/engine';
import { shuffle } from '@campfire/cards';
import { move, nextSeat, nthSpace, startPositions, withPosition, type TrackSpec } from '@campfire/track';

export const CANDY_COLORS = ['red', 'purple', 'yellow', 'blue', 'orange', 'green'] as const;
export type CandyColor = (typeof CANDY_COLORS)[number];

/** Squares 1..133 are the path; square 134 is the castle. */
export const CANDY_LENGTH = 134;
/** Named treat spaces (they have no colour); a picture card jumps straight to one. */
export const PICTURES: readonly { readonly name: string; readonly at: number }[] = [
  { name: 'Peppermint Forest', at: 20 },
  { name: 'Gumdrop Hills', at: 52 },
  { name: 'Lollipop Woods', at: 84 },
  { name: 'Toffee Lake', at: 114 },
];
/** Shortcuts: landing exactly on the entry carries you along. */
export const SHORTCUTS: readonly { readonly name: string; readonly from: number; readonly to: number }[] = [
  { name: 'Rainbow Bridge', from: 8, to: 44 },
  { name: 'Gumdrop Passage', from: 70, to: 100 },
];
/** Licorice spaces (coloured as usual; only sticky when the setting is on). */
export const LICORICE: readonly number[] = [30, 62, 94, 124];

export const CANDY_TRACK: TrackSpec = { length: CANDY_LENGTH, jumps: Object.fromEntries(SHORTCUTS.map((s) => [s.from, s.to])) };

export interface CandySpace {
  readonly color: CandyColor | null;
  readonly picture: number | null;
}

/** Index = square (0 = start, 134 = castle); colours cycle along the path. */
export const CANDY_SPACES: readonly CandySpace[] = Array.from({ length: CANDY_LENGTH + 1 }, (_, i) => {
  const picture = PICTURES.findIndex((p) => p.at === i);
  const onPath = i >= 1 && i < CANDY_LENGTH && picture < 0;
  return { color: onPath ? (CANDY_COLORS[(i - 1) % CANDY_COLORS.length] as CandyColor) : null, picture: picture < 0 ? null : picture };
});

export type CandyCard =
  | { readonly kind: 'color'; readonly color: CandyColor; readonly double: boolean }
  | { readonly kind: 'picture'; readonly picture: number };

/** 64 cards: per colour 7 singles + 3 doubles (60), plus one per picture (4). */
export function candyDeck(): CandyCard[] {
  const cards: CandyCard[] = [];
  for (const color of CANDY_COLORS) {
    for (let k = 0; k < 7; k++) cards.push({ kind: 'color', color, double: false });
    for (let k = 0; k < 3; k++) cards.push({ kind: 'color', color, double: true });
  }
  PICTURES.forEach((_, picture) => cards.push({ kind: 'picture', picture }));
  return cards;
}

export interface CandyOptions {
  /** Licorice spaces make you lose your next turn. */
  readonly sticky: boolean;
}

export interface CandyEvent {
  readonly seat: Seat;
  readonly card: CandyCard;
  readonly from: number;
  /** Square reached by the card, before any shortcut. */
  readonly landed: number;
  readonly to: number;
  readonly shortcut: string | null;
  readonly stuck: boolean;
  /** Seats whose turn was skipped (they were stuck) before the next player acted. */
  readonly skipped: readonly Seat[];
  readonly reshuffled: boolean;
}

export interface CandyState {
  readonly options: CandyOptions;
  readonly positions: readonly number[];
  readonly deck: readonly CandyCard[];
  readonly discard: readonly CandyCard[];
  readonly stuck: readonly boolean[];
  readonly turn: Seat;
  readonly last: CandyEvent | null;
  readonly result: Outcome | null;
}

export type CandyAction = { readonly type: 'draw' };

export interface CandyView {
  readonly you: Seat | null;
  readonly positions: readonly number[];
  readonly stuck: readonly boolean[];
  readonly turn: Seat;
  readonly drawCount: number;
  readonly last: CandyEvent | null;
  readonly result: Outcome | null;
  readonly sticky: boolean;
  readonly board: {
    readonly length: number;
    readonly colors: readonly (CandyColor | null)[];
    readonly pictures: typeof PICTURES;
    readonly shortcuts: typeof SHORTCUTS;
    readonly licorice: readonly number[];
  };
}

/** Where a card sends a piece standing on `from` (the castle when no such space remains). */
export function destination(card: CandyCard, from: number): number {
  if (card.kind === 'picture') return (PICTURES[card.picture] as { at: number }).at;
  return nthSpace(CANDY_SPACES, from, (sp) => sp.color === card.color, card.double ? 2 : 1) ?? CANDY_LENGTH;
}

export const cardName = (c: CandyCard): string =>
  c.kind === 'picture' ? (PICTURES[c.picture] as { name: string }).name : `${c.double ? 'double ' : ''}${c.color}`;

const COLORS_VIEW = CANDY_SPACES.map((s) => s.color);

export const candyland: GameDefinition<CandyState, CandyAction, CandyView, CandyOptions> = {
  id: 'candyland',
  name: 'Candy Path',
  rules: [
    'Be first to reach the Castle at the end of the path. It is all luck, with no decisions to make.',
    'Everyone starts off the board. On your turn you draw the top card; the app moves your piece for you.',
    'A colour card moves you to the next space of that colour. A double colour card moves you to the second next space of that colour.',
    'A picture card sends you straight to that treat (Peppermint Forest, Gumdrop Hills, Lollipop Woods or Toffee Lake), even if it is behind you.',
    'If there is no space of that colour left ahead of you, the card takes you to the Castle and you win.',
    'Land exactly on a shortcut entry (Rainbow Bridge or Gumdrop Passage) and you are carried along it automatically.',
    'Pieces may share a space. When the draw pile runs out, the discards are shuffled into a new one.',
    'Setting: Licorice spaces. Off by default; turn on Sticky and landing on a licorice space makes you lose your next turn.',
    'The app enforces everything; there is nothing for the table to agree on beyond who goes first (seat 1 always does).',
  ],
  minSeats: 2,
  maxSeats: 4,
  defaultOptions: { sticky: false },
  optionSpecs: [
    {
      key: 'sticky',
      label: 'Licorice spaces',
      help: 'Landing on licorice makes you lose your next turn.',
      choices: [
        { value: false, label: 'Just spaces' },
        { value: true, label: 'Sticky: lose a turn' },
      ],
    },
  ],

  setup({ seats, options, rng }) {
    return {
      options,
      positions: startPositions(seats),
      deck: shuffle(candyDeck(), rng),
      discard: [],
      stuck: Array.from({ length: seats }, () => false),
      turn: 0,
      last: null,
      result: null,
    };
  },

  activeSeats: (s) => (s.result ? [] : [s.turn]),

  validate(s, a, seat) {
    if (seat !== s.turn) return { ok: false, reason: 'not your turn' };
    return a.type === 'draw' ? { ok: true } : { ok: false, reason: 'unknown action' };
  },

  apply(s, _a, { rng }: { rng: Rng }) {
    const seat = s.turn;
    let deck = s.deck;
    let discard = s.discard;
    let reshuffled = false;
    if (deck.length === 0) {
      deck = shuffle(discard, rng);
      discard = [];
      reshuffled = true;
    }
    const card = deck[0] as CandyCard;
    deck = deck.slice(1);
    discard = [card, ...discard];

    const from = s.positions[seat] as number;
    const target = destination(card, from);
    // A move to a square is "exact": the destination is always on the board, so no overshoot arises.
    const m = move(CANDY_TRACK, from, target - from, 'exact');
    const won = m.to === CANDY_LENGTH;
    const stickyHere = s.options.sticky && !won && LICORICE.includes(m.to);
    const stuck = s.stuck.map((v, i) => (i === seat ? stickyHere : v));
    const shortcut = m.jump ? (SHORTCUTS.find((x) => x.from === m.landed) as { name: string }).name : null;

    // Pass the turn, skipping (and freeing) players who are stuck.
    let next = nextSeat(seat, s.positions.length);
    const skipped: Seat[] = [];
    if (!won) {
      while (stuck[next]) {
        stuck[next] = false;
        skipped.push(next);
        next = nextSeat(next, s.positions.length);
      }
    }
    const last: CandyEvent = { seat, card, from, landed: m.landed, to: m.to, shortcut, stuck: stickyHere, skipped, reshuffled };
    return {
      ...s,
      positions: withPosition(s.positions, seat, m.to),
      deck,
      discard,
      stuck,
      turn: won ? seat : next,
      last,
      result: won ? { winners: [seat], draw: false, reason: `${seat} reached the castle` } : null,
    };
  },

  view(s, viewer: Viewer) {
    return {
      you: viewer.kind === 'seat' ? viewer.seat : null,
      positions: s.positions,
      stuck: s.stuck,
      turn: s.turn,
      // Only the count of the draw pile is public; its order is not.
      drawCount: s.deck.length,
      last: s.last,
      result: s.result,
      sticky: s.options.sticky,
      board: { length: CANDY_LENGTH, colors: COLORS_VIEW, pictures: PICTURES, shortcuts: SHORTCUTS, licorice: LICORICE },
    };
  },

  outcome: (s) => s.result,

  enumerateActions: (s, seat) => (seat === s.turn && !s.result ? [{ type: 'draw' }] : []),
};
